import React, { Component } from 'react';
import { t, Trans } from '@lingui/macro';
import { withI18n } from '@lingui/react';
import { connect } from 'react-redux';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Form, Input, message, Modal, Row } from 'antd';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCardForm from '../../StripeCardForm';
import CouponActions from 'bukku/redux/Coupon/actions';
import CompanyActions from 'bukku/redux/Company/actions';
import AuthActions from 'bukku/redux/Auth/actions';
import Styled from './style';
import { CurrencyFormItem } from '../../FormItem/CompanyNew/CurrencyFormItem';
import { InventorySystemFormItem } from '../../FormItem/CompanyNew/InventorySystemFormItem';
import { ChartOfAccountFormItem } from '../../FormItem/CompanyNew/ChartOfAccountFormItem';
import { FullBillingMessage, FullTrialMessage } from './Messages';
import BukkuTooltip from '../../Tooltip';
import { CountriesFormItem } from '../../FormItem/CompanyNew/CountriesFormItem';

const { readAccountSets } = AuthActions;
const {
    read: readCoupon,
    reset: resetCoupon,
    set: setCoupon,
} = CouponActions;
const {
    create: createCompany,
} = CompanyActions;

const stripeKey = process.env.REACT_APP_STRIPE_KEY;
const stripePromise = loadStripe(stripeKey);

class CompanyNew extends Component {
    formRef = React.createRef();

    componentDidUpdate(prevProps) {
        const {
            visible,
            createState,
        } = this.props;

        // On visible
        if (visible && !prevProps.visible)
            this.onPageLoad();

        // Company created
        if (createState === 'success' && prevProps.createState === 'request')
            this.onCreateSuccess();
    }

    onPageLoad = () => {
        const { resetCoupon, readAccountSets } = this.props;
        readAccountSets();
        resetCoupon();
    };

    onCreateSuccess = () => {
        const {
            onCancel,
        } = this.props;

        const subdomain = this.formRef.current.getFieldValue('subdomain');
        onCancel(subdomain);
    };

    getSubdomainLabel = () => (
        <span>
            <Trans>Company URL</Trans>
            &nbsp;
            <BukkuTooltip
                title={
                    <>
                        <Trans>Create a unique URL to login and access your account.</Trans>
                    &nbsp;
                        <Trans>NOTE: The URL cannot be changed in the future.</Trans>
                    </>
                }
                // popupType="modal"
                popupContainerId="create-new-company-modal"
            >
                <QuestionCircleOutlined />
            </BukkuTooltip>
        </span>
    );

    getCompanyNameLabel = () => (
        <span>
            <Trans>Company Name</Trans>
            &nbsp;
            <BukkuTooltip
                title={<Trans>The legal name of your company.</Trans>}
                // popupType="modal"
                popupContainerId="create-new-company-modal"
            >
                <QuestionCircleOutlined />
            </BukkuTooltip>
        </span>
    );

    onCouponCodeBlur = e => {
        const {
            readCoupon,
            setCoupon,
        } = this.props;

        const couponCode = e.target.value;
        if (couponCode)
            readCoupon(couponCode, true);
        else
            setCoupon(null);
    };

    renderSubscriptionInfo = () => {
        const {
            coupon,
        } = this.props;

        if (coupon) {
            let message;
            if (coupon.type === 'trial')
                message = <FullTrialMessage coupon={coupon} />;

            else if (coupon.type === 'discount')
                message = <FullBillingMessage coupon={coupon} />;

            return (
                <Alert
                    message={t`Coupon Code Applied`}
                    description={message}
                    type="success"
                    showIcon
                />
            );
        }
        return null;
    };

    hasPayment = () => {
        const {
            coupon,
        } = this.props;

        if (coupon && coupon.type === 'discount')
            return true;
        return false;
    };

    asyncValidateForm = async () => { // Function that gives true or false;
        try {
            await this.formRef.current.validateFields();
            return true;
        }
        catch {
            message.warning(t`Please check for the fields highlighted in red.`);
            return false;
        }
    };

    // Actual submit
    onSubmit = (e, paymentMethodId = null) => {
        e.preventDefault();

        const {
            createCompany,
        } = this.props;

        this.formRef.current.validateFields().then(() => {
            // Form data
            const formData = this.formRef.current.getFieldsValue();
            formData.subdomain = formData.subdomain.toLowerCase();
            if (paymentMethodId)
                formData.payment_method_id = paymentMethodId;

            createCompany(formData);
        }).catch(() => {
            message.warning(t`Please check the fields highlighted in red.`);
            return false;
        });
    };

    renderAction = () => {
        const {
            isPushing,
        } = this.props;

        if (this.hasPayment()) {
            return (
                <Elements stripe={stripePromise}>
                    <div className="stripe-form">
                        <StripeCardForm
                            asyncValidateForm={this.asyncValidateForm}
                            onSubmit={this.onSubmit}
                            buttonLabel={t`Start Subscription`}
                        />
                    </div>
                </Elements>
            );
        }
        return (
            <div className="action">
                <Button type="primary" htmlType="submit" loading={isPushing} onClick={this.onSubmit}>
                    <Trans>Start Free Trial</Trans>
                </Button>
            </div>
        );
    };

    getDomain = () => `.${process.env.REACT_APP_URL_DOMAIN}`;

    render() {
        const {
            visible,
            onCancel,
            accountSets,
        } = this.props;

        return (
            <Modal
                id="create-new-company-modal"
                title={<Trans>New Company</Trans>}
                width={420}
                open={visible}
                destroyOnClose
                footer={null}
                onCancel={() => onCancel()}
            >
                <Styled>
                    <Form layout="vertical" ref={this.formRef} id="new-company-popup-parent">
                        <Row type="flex">
                            <Col span={24}>
                                <Form.Item
                                    name="legal_name"
                                    label={this.getCompanyNameLabel()}
                                    rules={[{
                                        required: true,
                                        message: t`Company name is required.`,
                                    }]}
                                >
                                    <Input placeholder={t`Eg. Bukku Sdn Bhd`} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <CountriesFormItem />
                        <CurrencyFormItem />
                        <InventorySystemFormItem />
                        <ChartOfAccountFormItem accountSets={accountSets} />
                        <Row type="flex">
                            <Col span={24}>
                                <Form.Item
                                    name="subdomain"
                                    label={this.getSubdomainLabel()}
                                    rules={[{
                                        required: true,
                                        message: t`URL is required.`,
                                    }, {
                                        min: 3,
                                        message: t`URL is too short.`,
                                    }, {
                                        max: 20,
                                        message: t`URL is too long.`,
                                    }, {
                                        pattern: "^[A-Za-z0-9]+$",
                                        message: t`Only alphabets and numbers are accepted for URL.`,
                                    }]}
                                >
                                    <Input
                                        addonBefore="https://"
                                        addonAfter={this.getDomain()}
                                        placeholder={t`Eg. demo`}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row type="flex">
                            <Col span={24}>
                                <Form.Item
                                    name="coupon_code"
                                    label={<Trans>Coupon Code</Trans>}
                                    rules={[{
                                        max: 20,
                                        message: t`The coupon code is too long.`,
                                    }]}
                                >
                                    <Input
                                        placeholder={t`Optional`}
                                        onBlur={this.onCouponCodeBlur}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {this.renderSubscriptionInfo()}

                        <div className="policies">
                            <p>
                                <Trans>
                                    By creating a new company, you agree to our&nbsp;
                                    <a
                                        href="https://bukku.my/terms"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Terms of Service&nbsp;
                                    </a>
                                    and&nbsp;
                                    <a
                                        href="https://bukku.my/privacy-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Privacy Policy
                                    </a>
                                    .
                                </Trans>
                            </p>
                        </div>

                        {this.renderAction()}
                    </Form>
                </Styled>
            </Modal>
        );
    }
}
export default connect(state => ({
    isPushing: state.App.isPushing,
    coupon: state.Coupon.coupon,
    createState: state.Company.createState,
    accountSets: state.Auth.accountSets,
}), {
    setCoupon,
    readCoupon,
    resetCoupon,
    createCompany,
    readAccountSets,
})(withI18n()(CompanyNew));
